import { useState } from "react";
import { ArrowRight, Boxes, Cloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import type { Account } from "../leads/types";

type AuthMode = "login" | "register" | "forgot";

export function AuthScreen({
  onLogin,
}: {
  onLogin: (account: Account) => void;
}) {
  const [mode, setMode] = useState<AuthMode>("login");
  const [username, setUsername] = useState("");
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [devResetUrl, setDevResetUrl] = useState("");
  const [error, setError] = useState("");

  const switchMode = (next: AuthMode) => {
    setMode(next);
    setError("");
    setMessage("");
    setDevResetUrl("");
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setDevResetUrl("");
    setBusy(true);
    try {
      if (mode === "forgot") {
        const result = await api<{ ok: true; devResetUrl?: string }>(
          "/api/auth/forgot",
          {
            method: "POST",
            body: JSON.stringify({ email: recoveryEmail }),
          },
        );
        setMessage(
          "如果该邮箱已绑定账号，重置链接会发送到邮箱。请检查收件箱和垃圾邮件。",
        );
        if (result.devResetUrl) setDevResetUrl(result.devResetUrl);
        return;
      }
      const result = await api<{ account: Account }>(`/api/auth/${mode}`, {
        method: "POST",
        body: JSON.stringify({ username, email: recoveryEmail, password }),
      });
      onLogin(result.account);
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "操作失败，请稍后重试。",
      );
    } finally {
      setBusy(false);
    }
  };

  const title =
    mode === "login"
      ? "登录内部工作区"
      : mode === "register"
        ? "创建账号"
        : "找回密码";

  return (
    <main className="grid min-h-dvh place-items-center bg-background p-4">
      <section className="w-full max-w-sm rounded-lg border bg-card p-6 sm:p-8">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 text-sm font-semibold">
            <Boxes className="size-[18px]" />
            货盘 Lite
          </div>
          <span
            className="inline-grid size-8 place-items-center rounded bg-emerald-400/10 text-emerald-300"
            title="内部云工作区"
            aria-label="内部云工作区"
          >
            <Cloud className="size-4" />
          </span>
        </div>
        <h1 className="mt-8 text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm leading-6 text-muted-foreground">
          {mode === "forgot"
            ? "输入账号绑定的找回邮箱，我们会发送密码重置链接。"
            : "内部共享货盘、会话与即时同步。"}
        </p>
        <form className="mt-6 space-y-4" onSubmit={submit}>
          {mode !== "forgot" && (
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              账号
              <Input
                type="text"
                required
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                className="h-10"
              />
            </label>
          )}
          {mode !== "login" && (
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              找回邮箱
              <Input
                type="email"
                required
                value={recoveryEmail}
                onChange={(event) => setRecoveryEmail(event.target.value)}
                autoComplete="email"
                className="h-10"
              />
            </label>
          )}
          {mode !== "forgot" && (
            <label className="block space-y-1.5 text-xs font-medium text-muted-foreground">
              密码
              <Input
                type="password"
                required
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete={
                  mode === "login" ? "current-password" : "new-password"
                }
                className="h-10"
              />
            </label>
          )}
          {error && (
            <p
              role="alert"
              className="rounded bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </p>
          )}
          {message && (
            <p
              role="status"
              className="rounded bg-emerald-400/10 px-3 py-2 text-sm text-emerald-300"
            >
              {message}
            </p>
          )}
          {devResetUrl && (
            <a
              href={devResetUrl}
              className="block rounded bg-accent px-3 py-2 text-sm text-accent-foreground underline underline-offset-4"
            >
              开发环境：打开重置链接
            </a>
          )}
          <Button type="submit" className="h-10 w-full" disabled={busy}>
            {busy
              ? "请稍候…"
              : mode === "login"
                ? "登录"
                : mode === "register"
                  ? "注册"
                  : "发送重置邮件"}
            <ArrowRight />
          </Button>
        </form>
        <div className="mt-4 flex flex-col items-center gap-1 text-sm">
          {mode !== "forgot" ? (
            <>
              <button
                type="button"
                className="min-h-10 w-full text-muted-foreground hover:text-foreground"
                onClick={() =>
                  switchMode(mode === "login" ? "register" : "login")
                }
              >
                {mode === "login" ? "没有账号？自助注册" : "已有账号？返回登录"}
              </button>
              <button
                type="button"
                className="min-h-10 w-full text-muted-foreground hover:text-foreground"
                onClick={() => switchMode("forgot")}
              >
                忘记密码？
              </button>
            </>
          ) : (
            <button
              type="button"
              className="min-h-10 w-full text-muted-foreground hover:text-foreground"
              onClick={() => switchMode("login")}
            >
              返回登录
            </button>
          )}
        </div>
      </section>
    </main>
  );
}
